import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2, Sparkles, Plus, Trash2, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_META, FLAG_META, humanFileSize } from "@/lib/exams";

const FLAG_OPTIONS = [
    { value: "", label: "—" },
    { value: "low", label: "Baixo" },
    { value: "normal", label: "Normal" },
    { value: "high", label: "Alto" },
    { value: "critical", label: "Crítico" },
];

export default function ExamUploadDialog({
    patientId,
    consultationId = null,
    onClose,
    onSaved,
}) {
    const [catalog, setCatalog] = useState([]);
    const [category, setCategory] = useState("laboratorial");
    const [examType, setExamType] = useState("");
    const [customLabel, setCustomLabel] = useState("");
    const [examDate, setExamDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [results, setResults] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [step, setStep] = useState("file"); // file | review

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/exams/catalog");
                setCatalog(data.categories || []);
                if (data.categories?.length) {
                    setCategory(data.categories[0].value);
                    setExamType(data.categories[0].types[0]?.value || "");
                }
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    const currentCategory = useMemo(
        () => catalog.find((c) => c.value === category) || null,
        [catalog, category]
    );

    useEffect(() => {
        // Reset examType when category changes
        if (currentCategory && !currentCategory.types.find((t) => t.value === examType)) {
            setExamType(currentCategory.types[0]?.value || "");
        }
    }, [currentCategory]); // eslint-disable-line

    const isLaboratorial = category === "laboratorial";
    const isPdf = file?.type === "application/pdf";
    const isImage = (file?.type || "").startsWith("image/");
    const canAiExtract = isPdf || isImage;

    const addRow = () =>
        setResults((r) => [...r, { analyte: "", value: "", unit: "", reference_range: "", flag: "" }]);
    const updateRow = (i, k, v) =>
        setResults((r) => r.map((x, ix) => (ix === i ? { ...x, [k]: v } : x)));
    const removeRow = (i) => setResults((r) => r.filter((_, ix) => ix !== i));

    const submit = async () => {
        if (!file) { toast.error("Selecione um arquivo."); return; }
        if (!examDate) { toast.error("Informe a data do exame."); return; }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("patient_id", patientId);
            form.append("exam_type", examType || "outro");
            form.append("exam_date", examDate);
            form.append("category", category);
            if (customLabel) form.append("exam_type_label", customLabel);
            if (consultationId) form.append("consultation_id", consultationId);
            if (notes) form.append("notes", notes);
            if (results.length) form.append("results_json", JSON.stringify(results.filter((r) => r.analyte)));
            form.append("file", file);
            const { data } = await api.post("/exams", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Exame anexado com sucesso.");
            onSaved?.(data);
            onClose?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const runAiExtract = async () => {
        // Envia o arquivo (PDF ou imagem), roda extração e mostra sugestão para revisão.
        if (!file) { toast.error("Selecione um arquivo antes de sugerir resultados."); return; }
        if (!canAiExtract) { toast.error("Extração automática funciona com PDFs e imagens."); return; }
        setExtracting(true);
        try {
            // First save exam (necessary because /extract works on server-side stored file)
            const form = new FormData();
            form.append("patient_id", patientId);
            form.append("exam_type", examType || "outro");
            form.append("exam_date", examDate);
            form.append("category", category);
            if (customLabel) form.append("exam_type_label", customLabel);
            if (consultationId) form.append("consultation_id", consultationId);
            if (notes) form.append("notes", notes);
            form.append("file", file);
            const { data: created } = await api.post("/exams", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Now run extraction
            const { data: ex } = await api.post(`/exams/${created.id}/extract`);
            const suggested = (ex.suggested_results || []).map((s) => ({
                analyte: s.analyte || "",
                value: s.value || "",
                unit: s.unit || "",
                reference_range: s.reference_range || "",
                method: s.method || "",
                flag: s.flag || "",
            }));
            setResults((prev) => [...prev, ...suggested]);
            // Autofill detected metadata when the user hasn't filled them
            const det = ex.detected || {};
            if (det.exam_date && /^\d{4}-\d{2}-\d{2}$/.test(det.exam_date)) {
                setExamDate(det.exam_date);
            }
            if (det.exam_type_label && !customLabel) setCustomLabel(det.exam_type_label);
            const modeLabel = ex.extraction_mode === "vision-image"
                ? "Vision (imagem)"
                : ex.extraction_mode === "vision-pdf"
                    ? "Vision (PDF escaneado)"
                    : "Texto (PDF)";
            toast.success(`${ex.count || suggested.length} resultado(s) transcritos via IA — ${modeLabel}. Revise antes de salvar.`);
            // Persist suggestions to the just-created exam
            try {
                await api.put(`/exams/${created.id}`, { results: suggested });
            } catch { /* ignore */ }
            onSaved?.(created);
            onClose?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setExtracting(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
            data-testid="exam-upload-dialog"
        >
            <div className="bg-[var(--bg-primary)] rounded-lg shadow-xl w-full max-w-3xl my-8">
                <div className="flex items-center justify-between p-5 border-b border-[var(--border-organic)]">
                    <div>
                        <h2 className="font-display text-2xl text-brand">Anexar exame</h2>
                        <p className="text-xs text-muted-clinical mt-0.5">
                            Aceita PDF ou imagem (JPG/PNG). Máximo 25 MB.
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="exam-upload-close" className="btn-ghost">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="label-clinical">Categoria</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="input-clinical"
                                data-testid="exam-category-select"
                            >
                                {catalog.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {CATEGORY_META[c.value]?.label || c.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label-clinical">Tipo de exame</label>
                            <select
                                value={examType}
                                onChange={(e) => setExamType(e.target.value)}
                                className="input-clinical"
                                data-testid="exam-type-select"
                            >
                                {(currentCategory?.types || []).map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(examType === "outro" || examType?.startsWith("outros_")) && (
                        <div>
                            <label className="label-clinical">Descrição personalizada</label>
                            <input
                                type="text"
                                value={customLabel}
                                onChange={(e) => setCustomLabel(e.target.value)}
                                placeholder="Ex.: Beta-HCG quantitativo"
                                className="input-clinical"
                                data-testid="exam-custom-label"
                            />
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="label-clinical">Data do exame</label>
                            <input
                                type="date"
                                value={examDate}
                                onChange={(e) => setExamDate(e.target.value)}
                                className="input-clinical"
                                data-testid="exam-date-input"
                            />
                        </div>
                        <div>
                            <label className="label-clinical">Arquivo</label>
                            <input
                                type="file"
                                accept="application/pdf,image/*"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="input-clinical file:mr-3 file:border-0 file:bg-alt file:text-brand file:px-3 file:py-1.5 file:rounded file:cursor-pointer"
                                data-testid="exam-file-input"
                            />
                            {file && (
                                <p className="text-xs text-muted-clinical mt-1 flex items-center gap-1">
                                    <FileText size={12} /> {file.name} · {humanFileSize(file.size)}
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="label-clinical">Observações</label>
                        <textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="input-clinical"
                            placeholder="Notas sobre o contexto clínico ou o laudo"
                            data-testid="exam-notes-input"
                        />
                    </div>

                    {isLaboratorial && (
                        <div className="card-earthy bg-alt/50">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="font-medium text-brand text-sm">Resultados estruturados (opcional)</h3>
                                    <p className="text-xs text-muted-clinical mt-0.5">
                                        Cadastre analitos → alimentam o gráfico de tendência.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    {canAiExtract && (
                                        <button
                                            type="button"
                                            onClick={runAiExtract}
                                            disabled={extracting || uploading || !file}
                                            className="btn-secondary text-xs"
                                            data-testid="exam-ai-extract-btn"
                                            title="Envia o arquivo (PDF ou imagem) para a IA transcrever fielmente TODOS os analitos com valores e referências. O exame é salvo já com esses valores para você revisar depois."
                                        >
                                            {extracting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                            Transcrever com IA
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={addRow}
                                        className="btn-ghost text-xs"
                                        data-testid="exam-add-result-btn"
                                    >
                                        <Plus size={12} /> Adicionar linha
                                    </button>
                                </div>
                            </div>
                            {results.length === 0 ? (
                                <p className="text-xs text-muted-clinical italic">
                                    Nenhum resultado ainda. Você pode salvar sem estruturar e digitar depois.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-brand-secondary font-medium">
                                        <div className="col-span-4">Analito</div>
                                        <div className="col-span-2">Valor</div>
                                        <div className="col-span-2">Unidade</div>
                                        <div className="col-span-2">Referência</div>
                                        <div className="col-span-1">Flag</div>
                                        <div className="col-span-1"></div>
                                    </div>
                                    {results.map((r, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`exam-result-row-${i}`}>
                                            <input className="input-clinical col-span-4 py-1 text-sm" placeholder="Hemoglobina" value={r.analyte} onChange={(e) => updateRow(i, "analyte", e.target.value)} />
                                            <input className="input-clinical col-span-2 py-1 text-sm" placeholder="13.5" value={r.value} onChange={(e) => updateRow(i, "value", e.target.value)} />
                                            <input className="input-clinical col-span-2 py-1 text-sm" placeholder="g/dL" value={r.unit} onChange={(e) => updateRow(i, "unit", e.target.value)} />
                                            <input className="input-clinical col-span-2 py-1 text-sm" placeholder="12-16" value={r.reference_range} onChange={(e) => updateRow(i, "reference_range", e.target.value)} />
                                            <select className="input-clinical col-span-1 py-1 text-sm" value={r.flag} onChange={(e) => updateRow(i, "flag", e.target.value)}>
                                                {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                            <button type="button" onClick={() => removeRow(i)} className="text-muted-clinical hover:text-red-600 col-span-1" data-testid={`exam-remove-result-${i}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-[var(--border-organic)] p-4 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost" data-testid="exam-upload-cancel">Cancelar</button>
                    <button
                        onClick={submit}
                        disabled={uploading || extracting || !file}
                        className="btn-primary"
                        data-testid="exam-upload-submit"
                    >
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        Salvar exame
                    </button>
                </div>
            </div>
        </div>
    );
}
